import React from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { 
  ArrowRight, 
  BookOpen, 
  Users, 
  Code
} from "lucide-react";
import { 
  Container, 
  Section, 
  Grid, 
  Card, 
  Badge
} from "../styles/GlobalStyles";

const CTASection = styled(Section)`
  background: linear-gradient(135deg, var(--primary-600) 0%, var(--secondary-600) 50%, var(--accent-600) 100%);
  color: var(--gray-0);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(255, 255, 255, 0.08) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: var(--space-16);
  
  @media (max-width: 768px) {
    margin-bottom: var(--space-12);
  }
  
  .badge {
    margin-bottom: var(--space-4);
    background: rgba(255, 255, 255, 0.2);
    color: var(--gray-0);
    border: 1px solid rgba(255, 255, 255, 0.3);
  }
  
  h2 {
    font-size: var(--text-5xl);
    margin-bottom: var(--space-6);
    font-weight: 800;
    
    @media (max-width: 768px) {
      font-size: var(--text-4xl);
    }
    
    @media (max-width: 480px) {
      font-size: var(--text-3xl);
    }
  }
  
  .subtitle {
    font-size: var(--text-xl);
    opacity: 0.9;
    max-width: 600px;
    margin: 0 auto;
    line-height: var(--leading-relaxed);
    
    @media (max-width: 768px) {
      font-size: var(--text-lg);
      padding: 0 var(--space-4);
    }
  }
`;

const CTAGrid = styled(Grid)`
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: var(--space-8);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: var(--space-6);
  }
`;

const CTACard = styled(Card)`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(20px);
  color: var(--gray-0);
  padding: var(--space-8);
  text-align: center;
  transition: all var(--transition-normal);
  
  @media (max-width: 768px) {
    padding: var(--space-6);
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
    
    @media (max-width: 768px) {
      transform: translateY(-4px);
    }
    
    .icon-container {
      transform: scale(1.1) rotate(5deg);
    }
  }
  
  .icon-container {
    width: 80px;
    height: 80px;
    margin: 0 auto var(--space-6);
    background: rgba(255, 255, 255, 0.2);
    border-radius: var(--radius-3xl);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all var(--transition-normal);
    
    @media (max-width: 768px) {
      width: 70px;
      height: 70px;
      margin-bottom: var(--space-4);
    }
  }
  
  h3 {
    font-size: var(--text-2xl);
    margin-bottom: var(--space-4);
    font-weight: 700;
    
    @media (max-width: 768px) {
      font-size: var(--text-xl);
    }
  }
  
  p {
    opacity: 0.9;
    margin-bottom: var(--space-6);
    line-height: var(--leading-relaxed);
    
    @media (max-width: 768px) {
      font-size: var(--text-sm);
      margin-bottom: var(--space-4);
    }
  }
  
  .cta-button {
    background: var(--gray-0);
    color: var(--primary-600);
    border: none;
    padding: var(--space-3) var(--space-6);
    border-radius: var(--radius-xl);
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: var(--space-2);
    transition: all var(--transition-normal);
    margin: 0 auto;
    
    &:hover {
      background: var(--primary-50);
      transform: scale(1.05);
    }
  }
`;

const QuickActions = () => {
  const navigate = useNavigate();

  const actions = [
    {
      icon: <BookOpen size={32} />,
      title: "Career Learning Paths",
      description: "Structured 1-3 year programs in Full-Stack Development, AI/ML, and IoT",
      buttonText: "Explore Paths",
      path: "/career-paths"
    },
    {
      icon: <Users size={32} />,
      title: "Internship Programs", 
      description: "Gain real-world experience with our comprehensive internship opportunities",
      buttonText: "Apply Now",
      path: "/internships"
    }
  ];

  return (
    <CTASection>
      <Container>
        <SectionHeader>
          <Badge className="badge">
            <Code size={16} />
            Transform Your Career
          </Badge>
          <h2>Ready to Launch Your Tech Career?</h2>
          <p className="subtitle">
            Explore our comprehensive programs designed to take you from beginner to industry expert
          </p>
        </SectionHeader>

        <CTAGrid>
          {actions.map((action, index) => (
            <CTACard key={index}>
              <div className="icon-container">
                {action.icon}
              </div>
              <h3>{action.title}</h3>
              <p>{action.description}</p>
              <button 
                className="cta-button"
                onClick={() => navigate(action.path)}
              >
                {action.buttonText}
                <ArrowRight size={16} />
              </button>
            </CTACard>
          ))}
        </CTAGrid>
      </Container>
    </CTASection>
  );
};

export default QuickActions;
